import React, { useState, useEffect } from 'react';
import { formatDateOnly } from '../../utils/dates';
import { API_BASE } from '../../config';
import MedicalFileForm from './MedicalFileForm';
import HealthRecordsHub from '../HealthRecordsHub';
import UhcExclusions from '../UhcExclusions';
import UhcAuditReports from '../UhcAuditReports';
import { useConfirm } from '../ConfirmProvider';

function RecordsPage({ fetchWithAuth, setActiveTab }) {
  const confirm = useConfirm();
  const [medicalFiles, setMedicalFiles] = useState([]);
  const [showMedicalFileForm, setShowMedicalFileForm] = useState(false);
  const [editingMedicalFile, setEditingMedicalFile] = useState(null);
  const [viewingMedicalFile, setViewingMedicalFile] = useState(null);
  const [medicalFileBlobUrl, setMedicalFileBlobUrl] = useState(null);
  const [medicalFileLoading, setMedicalFileLoading] = useState(false);
  const [recordsSubTab, setRecordsSubTab] = useState('health-records');
  const [aiContextData, setAiContextData] = useState(null);
  const [aiContextLoading, setAiContextLoading] = useState(false);

  useEffect(() => {
    loadMedicalFiles();
  }, []);

  useEffect(() => {
    if (recordsSubTab === 'ai-data') {
      loadAiContext();
    }
  }, [recordsSubTab]);

  useEffect(() => {
    const handleTabNav = (e) => { if (e.detail) setActiveTab(e.detail); };
    const handleSubTabNav = (e) => { if (e.detail) setRecordsSubTab(e.detail); };
    window.addEventListener('navigate-tab', handleTabNav);
    window.addEventListener('navigate-subtab', handleSubTabNav);
    return () => {
      window.removeEventListener('navigate-tab', handleTabNav);
      window.removeEventListener('navigate-subtab', handleSubTabNav);
    };
  }, [setActiveTab]);

  const loadMedicalFiles = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/medical-records/`);
      const data = await response.json();
      setMedicalFiles(data);
    } catch (error) {
      console.error('Failed to load medical records:', error);
    }
  };

  const loadAiContext = async () => {
    setAiContextLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/profile/ai-context`);
      const data = await response.json();
      setAiContextData(data);
    } catch (error) {
      console.error('Failed to load AI context:', error);
    } finally {
      setAiContextLoading(false);
    }
  };

  const handleEditMedicalFile = (file) => {
    setEditingMedicalFile(file);
    setShowMedicalFileForm(true);
  };

  const handleDeleteMedicalFile = async (fileId) => {
    if (await confirm({
      title: 'Delete medical file?',
      message: 'This file will be permanently removed from your records, along with anything extracted from it into your health context. This cannot be undone.',
      confirmLabel: 'Delete File',
      destructive: true,
    })) {
      try {
        const response = await fetchWithAuth(`${API_BASE}/medical-records/${fileId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadMedicalFiles();
        }
      } catch (error) {
        console.error('Failed to delete medical file:', error);
      }
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/medical-records/${fileId}/download`, {
        method: 'GET'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download file');
      }
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file');
    }
  };

  const handleViewMedicalFile = async (file) => {
    setViewingMedicalFile(file);
    setMedicalFileLoading(true);
    setMedicalFileBlobUrl(null);
    try {
      const response = await fetchWithAuth(`${API_BASE}/medical-records/${file.id}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setMedicalFileBlobUrl(url);
        // Keep medicalFileLoading=true — cleared by iframe onLoad below
      } else {
        setMedicalFileLoading(false);
      }
    } catch (error) {
      console.error('Failed to load medical file:', error);
      setMedicalFileLoading(false);
    }
  };

  const closeMedicalFileViewer = () => {
    if (medicalFileBlobUrl) {
      window.URL.revokeObjectURL(medicalFileBlobUrl);
      setMedicalFileBlobUrl(null);
    }
    setViewingMedicalFile(null);
  };

  const closeMedicalFileForm = () => {
    setShowMedicalFileForm(false);
    setEditingMedicalFile(null);
  };

  // Helper to render AI context sections
  const renderAiDataSection = (title, content) => (
    <div className="aidp-section">
      <h3 className="aidp-section-title">{title}</h3>
      {content}
    </div>
  );

  const renderKeyValue = (pairs) => (
    <dl className="aidp-data-grid">
      {pairs.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value != null && value !== '' ? String(value) : <span className="aidp-empty">Not recorded</span>}</dd>
        </React.Fragment>
      ))}
    </dl>
  );

  const renderList = (items, renderItem) => {
    if (!items || items.length === 0) {
      return <p className="aidp-empty">No data recorded</p>;
    }
    return (
      <ul className="aidp-list">
        {items.map((item, i) => <li key={i}>{renderItem ? renderItem(item) : String(item)}</li>)}
      </ul>
    );
  };

  const renderMedRecordGroup = (title, recs) => {
    if (!recs || recs.length === 0) return null;
    return (
      <div className="aidp-fhir-group">
        <h4 className="aidp-fhir-heading">{title} <span className="aidp-list-detail">({recs.length})</span></h4>
        <ul className="aidp-list">
          {recs.slice(0, 20).map((rec, i) => (
            <li key={i} className="aidp-list-item">
              <span className="aidp-list-label">{rec.record_name || rec.record_type}</span>
              {rec.record_date && <span className="aidp-list-detail">{rec.record_date}</span>}
              {rec.description && <div className="aidp-list-detail aidp-list-desc">{rec.description.substring(0, 300)}</div>}
            </li>
          ))}
          {recs.length > 20 && <li className="aidp-list-item"><span className="aidp-list-detail">...and {recs.length - 20} more</span></li>}
        </ul>
      </div>
    );
  };

  const renderAiDataTab = () => {
    if (aiContextLoading) {
      return <div className="aidp-loading">Loading your UHC data set...</div>;
    }
    if (!aiContextData) {
      return <div className="aidp-loading">No UHC data available. Use the app to start building your profile.</div>;
    }

    const uc = aiContextData.context_json || {};
    const body = aiContextData.body_characteristics || {};
    const device = uc.device_data || {};

    // Build medical records index by type — include all sources, filter blank entries
    const allMedRecords = (uc.medical_records || []).filter(r =>
      (r.record_name && r.record_name.trim()) || (r.description && r.description.trim())
    );
    const recordsByType = {};
    allMedRecords.forEach(r => {
      const t = r.record_type || 'other';
      if (!recordsByType[t]) recordsByType[t] = [];
      recordsByType[t].push(r);
    });

    const exerciseLabels = ['None', 'Light (1-2 days/week)', 'Moderate (3-4 days/week)', '', 'Heavy (5-6 days/week)', 'Super (7 days/week)'];

    // Allergies: combine context_json.allergies + medical_records allergy_detail, dedup by name
    const rawAllergyList = uc.allergies || [];
    const allergyRecords = recordsByType['allergy_detail'] || [];
    // Build a map: name → best detail (prefer record with description)
    const allergyMap = new Map();
    rawAllergyList.forEach(a => {
      const name = (typeof a === 'string' ? a : (a.name || a.allergy_name || '')).trim().toLowerCase();
      if (name && !allergyMap.has(name)) allergyMap.set(name, { name: typeof a === 'string' ? a : (a.name || a.allergy_name), description: null, record_date: null });
    });
    allergyRecords.forEach(r => {
      const name = (r.record_name || '').trim().toLowerCase();
      if (name) allergyMap.set(name, { name: r.record_name, description: r.description, record_date: r.record_date });
    });
    const mergedAllergies = Array.from(allergyMap.values());
    const hasAllergies = mergedAllergies.length > 0;

    // Conditions: combine context_json.medical_conditions + medical_records condition, dedup
    const rawConditionList = uc.medical_conditions || [];
    const conditionRecords = recordsByType['condition'] || [];
    const conditionMap = new Map();
    rawConditionList.forEach(c => {
      const name = (typeof c === 'string' ? c : (c.name || '')).trim().toLowerCase();
      if (name && !conditionMap.has(name)) conditionMap.set(name, { name: typeof c === 'string' ? c : c.name, description: null, record_date: null });
    });
    conditionRecords.forEach(r => {
      const name = (r.record_name || '').trim().toLowerCase();
      if (name) conditionMap.set(name, { name: r.record_name, description: r.description, record_date: r.record_date });
    });
    const mergedConditions = Array.from(conditionMap.values());

    // Prescriptions: split into active and discontinued
    const allPrescriptions = aiContextData.prescriptions || [];
    const activeMeds = allPrescriptions.filter(p => p.is_active);
    const discontinuedMeds = allPrescriptions.filter(p => !p.is_active);
    const hasMedications = activeMeds.length > 0 || discontinuedMeds.length > 0;

    // Food preferences: exclude type='allergy'
    const foodPrefs = (aiContextData.food_preferences || []).filter(p => p.preference_type !== 'allergy');

    // Fitness & Training: combine fitness programs, exercise plans, weight training plans
    const fitnessPrograms = uc.active_fitness_programs || [];
    const exercisePlans = uc.active_exercise_plans || [];
    const weightPlans = uc.active_weight_training_plans || [];
    const hasFitness = fitnessPrograms.length > 0 || exercisePlans.length > 0 || weightPlans.length > 0;

    // Saved recipes summary
    const savedRecipes = uc.saved_recipes || [];
    const favCount = savedRecipes.filter(r => r.is_favorite).length;

    // Helper: only render section if it has data
    const conditionalSection = (title, items, renderFn) => {
      if (!items || items.length === 0) return null;
      return renderAiDataSection(title, renderFn(items));
    };

    // Helper: check if key-value pairs have any non-null value
    const hasAnyValue = (pairs) => pairs.some(([, v]) => v != null && v !== '');

    const profilePairs = [
      ['First Name', uc.first_name],
      ['Age', body.age || uc.age],
      ['Gender', body.birth_gender],
      ['Date of Birth', uc.date_of_birth],
    ];

    const bodyPairs = [
      ['Height', body.height ? `${body.height} ${body.height_unit || ''}` : null],
      ['Weight', body.weight ? `${body.weight} ${body.weight_unit || ''}` : null],
      ['Waist', body.waist_circumference ? `${body.waist_circumference} ${body.waist_unit || ''}` : null],
      ['Estimated BMI', body.estimated_bmi],
      // 'Recommended BMI' removed 2026-10-17 (D1): an uncited AI guess, no longer produced.
      ['Actual BMI', body.actual_bmi],
      ['Cardio Exercise Level', body.cardio_exercise_level != null ? (exerciseLabels[body.cardio_exercise_level] || body.cardio_exercise_level) : null],
      ['Weight Training Level', body.weight_training_level != null ? (exerciseLabels[body.weight_training_level] || body.weight_training_level) : null],
    ];

    const devicePairs = [
      ['Daily Steps (avg)', device.avg_daily_steps],
      ['Active Minutes (avg)', device.avg_active_minutes],
      ['Calories Burned (avg)', device.avg_daily_calories],
      ['Resting Heart Rate', device.resting_heart_rate ? `${device.resting_heart_rate} bpm` : null],
      ['Average Heart Rate', device.average_heart_rate ? `${device.average_heart_rate} bpm` : null],
      ['SpO2', device.spo2 ? `${device.spo2}%` : null],
      ['Respiratory Rate', device.respiratory_rate],
      ['VO2 Max', device.vo2_max],
      ['HRV (RMSSD)', device.hrv_rmssd],
      ['HRV (SDNN)', device.hrv_sdnn],
      ['Sleep (avg hours)', device.avg_sleep_hours],
      ['Sleep Score (avg)', device.avg_sleep_score],
      ['Sleep Efficiency', device.avg_sleep_efficiency ? `${device.avg_sleep_efficiency}%` : null],
      ['Recovery Score', device.recovery_score],
      ['Readiness Score', device.readiness_score],
      ['Training Readiness', device.training_readiness_score],
      ['Stress Level', device.stress_level],
      ['Activity Level', device.derived_activity_level],
      ['Daily Water Intake (avg)', device.avg_daily_water_ml ? `${device.avg_daily_water_ml} ml` : null],
    ];

    return (
      <div className="aidp-container">
        <h2 className="aidp-uhcTitle">My UHC Data Set</h2>

        {/* 1. Profile Basics */}
        {hasAnyValue(profilePairs) && renderAiDataSection('Profile Basics', renderKeyValue(profilePairs))}

        {/* 2. Body Characteristics */}
        {hasAnyValue(bodyPairs) && renderAiDataSection('Body Characteristics', renderKeyValue(bodyPairs))}

        {/* 3. Allergies & Restrictions */}
        {hasAllergies && renderAiDataSection('Allergies & Restrictions', (
          <ul className="aidp-list">
            {mergedAllergies.map((a, i) => (
              <li key={`a-${i}`} className="aidp-list-item">
                <span className="aidp-list-label">{a.name}</span>
                {a.description && <div className="aidp-list-detail aidp-list-desc">{a.description}</div>}
                {a.record_date && <span className="aidp-list-detail">{a.record_date}</span>}
              </li>
            ))}
          </ul>
        ))}

        {/* 4. Conditions */}
        {mergedConditions.length > 0 && renderAiDataSection('Conditions', (
          <ul className="aidp-list">
            {mergedConditions.map((c, i) => (
              <li key={`c-${i}`} className="aidp-list-item">
                <span className="aidp-list-label">{c.name}</span>
                {c.description && <div className="aidp-list-detail aidp-list-desc">{c.description}</div>}
                {c.record_date && <span className="aidp-list-detail">{c.record_date}</span>}
              </li>
            ))}
          </ul>
        ))}

        {/* 5. Current Medications */}
        {hasMedications && (
          <>
            {renderAiDataSection('Current Medications',
              activeMeds.length > 0 ? (
                <ul className="aidp-list">
                  {activeMeds.map((med, i) => (
                    <li key={i} className="aidp-list-item">
                      <span className="aidp-list-label">{med.medication_name}</span>
                      {med.dosage && <span className="aidp-list-detail">{med.dosage}</span>}
                      {med.frequency && <span className="aidp-list-detail">{med.frequency}</span>}
                      {med.reason && <span className="aidp-list-detail">Reason: {med.reason}</span>}
                      {med.timing_instructions && <span className="aidp-list-detail">Timing: {med.timing_instructions}</span>}
                      {med.start_date && <span className="aidp-list-detail">Since: {med.start_date}</span>}
                    </li>
                  ))}
                </ul>
              ) : <p className="aidp-empty">No active medications</p>
            )}
            {discontinuedMeds.length > 0 && renderAiDataSection('Discontinued Medications', (
              <ul className="aidp-list">
                {discontinuedMeds.map((med, i) => (
                  <li key={i} className="aidp-list-item">
                    <span className="aidp-list-label">{med.medication_name}</span>
                    {med.dosage && <span className="aidp-list-detail">{med.dosage}</span>}
                    {med.frequency && <span className="aidp-list-detail">{med.frequency}</span>}
                    {med.reason && <span className="aidp-list-detail">Reason: {med.reason}</span>}
                    {med.start_date && <span className="aidp-list-detail">{med.start_date}{med.end_date ? ` — ${med.end_date}` : ''}</span>}
                  </li>
                ))}
              </ul>
            ))}
          </>
        )}

        {/* 6. Lab Results */}
        {(recordsByType['lab_result'] || []).length > 0 && renderAiDataSection('Lab Results', renderMedRecordGroup('Lab Results', recordsByType['lab_result']))}

        {/* 7. Vital Signs */}
        {(recordsByType['vital_signs'] || []).length > 0 && renderAiDataSection('Vital Signs', renderMedRecordGroup('Vital Signs', recordsByType['vital_signs']))}

        {/* 8. Immunizations */}
        {(recordsByType['vaccination'] || []).length > 0 && renderAiDataSection('Immunizations', renderMedRecordGroup('Immunizations', recordsByType['vaccination']))}

        {/* 9. Procedures */}
        {(recordsByType['procedure'] || []).length > 0 && renderAiDataSection('Procedures', renderMedRecordGroup('Procedures', recordsByType['procedure']))}

        {/* 10. Care Transitions */}
        {(recordsByType['care_transition'] || []).length > 0 && renderAiDataSection('Care Transitions', renderMedRecordGroup('Care Transitions', recordsByType['care_transition']))}

        {/* 11. Care Team */}
        {(recordsByType['provider'] || []).length > 0 && renderAiDataSection('Care Team', renderMedRecordGroup('Care Team', recordsByType['provider']))}

        {/* 12. Family Medical History */}
        {conditionalSection('Family Medical History', aiContextData.family_history || aiContextData.family_medical_history, (items) => renderList(items, (item) => (
          <span>
            <span className="aidp-list-label">{item.condition}</span>
            {(item.father || item.mother) && (
              <span className="aidp-list-detail">
                ({[item.father && 'Father', item.mother && 'Mother'].filter(Boolean).join(', ')})
              </span>
            )}
          </span>
        )))}

        {/* 13. Health Objectives */}
        {conditionalSection('Health Objectives', aiContextData.health_objectives, (items) => renderList(items, (obj) => (
          <span>
            <span className="aidp-list-label">{obj.title}</span>
            {obj.target_value && <span className="aidp-list-detail">Target: {obj.target_value} {obj.unit || ''}</span>}
          </span>
        )))}

        {/* 14. Food Preferences (excluding allergy type) */}
        {foodPrefs.length > 0 && renderAiDataSection('Food Preferences', renderList(foodPrefs, (pref) => (
          <span>
            <span className="aidp-list-label">{pref.name}</span>
            {pref.preference_type && <span className="aidp-list-detail">({pref.preference_type})</span>}
            {pref.ranking && <span className="aidp-list-detail">Rank: {pref.ranking}</span>}
          </span>
        )))}

        {/* 15. Diet Plans */}
        {conditionalSection('Diet Plans', aiContextData.diet_plans, (items) => renderList(items, (plan) => (
          <span>
            <span className="aidp-list-label">{plan.diet_name || plan.name}</span>
            {plan.category && <span className="aidp-list-detail">({plan.category})</span>}
          </span>
        )))}

        {/* 16. Fitness & Training */}
        {hasFitness && renderAiDataSection('Fitness & Training', (
          <div>
            {fitnessPrograms.length > 0 && (
              <div className="aidp-fhir-group">
                <h4 className="aidp-fhir-heading">Fitness Programs <span className="aidp-list-detail">({fitnessPrograms.length})</span></h4>
                {renderList(fitnessPrograms, (prog) => (
                  <span>
                    <span className="aidp-list-label">{prog.name}</span>
                    {prog.goal && <span className="aidp-list-detail">Goal: {prog.goal}</span>}
                    {prog.current_week && prog.duration_weeks && (
                      <span className="aidp-list-detail">Week {prog.current_week}/{prog.duration_weeks}</span>
                    )}
                    {prog.level && <span className="aidp-list-detail">{prog.level}</span>}
                  </span>
                ))}
              </div>
            )}
            {exercisePlans.length > 0 && (
              <div className="aidp-fhir-group">
                <h4 className="aidp-fhir-heading">Exercise Plans <span className="aidp-list-detail">({exercisePlans.length})</span></h4>
                {renderList(exercisePlans, (plan) => (
                  <span>
                    <span className="aidp-list-label">{plan.name}</span>
                    {plan.intensity && <span className="aidp-list-detail">{plan.intensity}</span>}
                    {plan.duration_days && <span className="aidp-list-detail">{plan.duration_days} days</span>}
                  </span>
                ))}
              </div>
            )}
            {weightPlans.length > 0 && (
              <div className="aidp-fhir-group">
                <h4 className="aidp-fhir-heading">Weight Training Plans <span className="aidp-list-detail">({weightPlans.length})</span></h4>
                {renderList(weightPlans, (plan) => (
                  <span>
                    <span className="aidp-list-label">{plan.name}</span>
                    {plan.type && <span className="aidp-list-detail">{plan.type}</span>}
                    {plan.intensity && <span className="aidp-list-detail">{plan.intensity}</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* 17. Meal Plans */}
        {conditionalSection('Meal Plans', uc.active_meal_plans, (items) => renderList(items, (plan) => (
          <span>
            <span className="aidp-list-label">{plan.name}</span>
            {plan.diet_category && <span className="aidp-list-detail">{plan.diet_category}</span>}
            {plan.target_calories && <span className="aidp-list-detail">{plan.target_calories} cal/day</span>}
          </span>
        )))}

        {/* 18. Saved Recipes */}
        {savedRecipes.length > 0 && renderAiDataSection(`Saved Recipes (${savedRecipes.length}${favCount > 0 ? `, ${favCount} favorites` : ''})`, renderList(savedRecipes, (recipe) => (
          <span>
            <span className="aidp-list-label">{recipe.title}</span>
            {recipe.cuisine_type && <span className="aidp-list-detail">{recipe.cuisine_type}</span>}
            {recipe.calories && <span className="aidp-list-detail">{recipe.calories} cal</span>}
            {recipe.is_favorite && <span className="aidp-list-badge aidp-list-badge--fav">Favorite</span>}
          </span>
        )))}

        {/* 19. Sports & Hobbies */}
        {conditionalSection('Sports & Hobbies', aiContextData.sports_hobbies, (items) => renderList(items, (sport) => (
          <span>
            <span className="aidp-list-label">{sport.activity}</span>
            {sport.skill_level && <span className="aidp-list-detail">{sport.skill_level}</span>}
            {sport.frequency && <span className="aidp-list-detail">({sport.frequency})</span>}
            {sport.is_primary && <span className="aidp-list-detail">[Primary]</span>}
          </span>
        )))}

        {/* 20. Device Data */}
        {hasAnyValue(devicePairs) && renderAiDataSection('Device Data', renderKeyValue(devicePairs))}

        {/* 21. Clinical Documents */}
        {(recordsByType['clinical_document'] || []).length > 0 && renderAiDataSection('Clinical Documents', renderMedRecordGroup('Clinical Documents', recordsByType['clinical_document']))}

        {/* Connected Devices */}
        {conditionalSection('Connected Devices', uc.connected_devices, (items) => renderList(items, (dev) => (
          <span>
            <span className="aidp-list-label">{dev.name}</span>
            {dev.type && <span className="aidp-list-detail">{dev.type}</span>}
            {dev.last_sync && <span className="aidp-list-detail">Last sync: {new Date(dev.last_sync).toLocaleDateString()}</span>}
          </span>
        )))}

        {/* Provider Recommendations */}
        {conditionalSection('Provider Recommendations', uc.provider_recommendations, (items) => renderList(items, (rec) => (
          <span>
            <span className="aidp-list-label">{rec.question || rec.topic || 'Recommendation'}</span>
            {rec.response && <span className="aidp-list-detail">{rec.response}</span>}
            {rec.provider_name && <span className="aidp-list-detail">by {rec.provider_name}</span>}
          </span>
        )))}

        {/* Trainer Recommendations */}
        {conditionalSection('Trainer Recommendations', uc.trainer_recommendations, (items) => renderList(items, (rec) => (
          <span>
            <span className="aidp-list-label">{rec.question || rec.topic || 'Recommendation'}</span>
            {rec.response && <span className="aidp-list-detail">{rec.response}</span>}
            {rec.trainer_name && <span className="aidp-list-detail">by {rec.trainer_name}</span>}
          </span>
        )))}

        <div className="aidp-footer-message">
          This is the data Healist uses to power ALL AI recommendations. Any time a change is made in Healist, this data is automatically updated.
        </div>

        {aiContextData.last_updated && (
          <div className="aidp-timestamp">
            Last updated: {new Date(aiContextData.last_updated).toLocaleString()}
            {aiContextData.last_trigger_source && ` (triggered by: ${aiContextData.last_trigger_source})`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Medical Records</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>← Dashboard</button>
        </div>

        <div className="page-nav-bar">
          {/* Sub-Tab Navigation */}
          <div className="tab-bar">
            <button
              className={`tab-item${recordsSubTab === 'health-records' ? ' tab-item--active' : ''}`}
              onClick={() => setRecordsSubTab('health-records')}
            >
              Health Records
            </button>
            <button
              className={`tab-item${recordsSubTab === 'files' ? ' tab-item--active' : ''}`}
              onClick={() => setRecordsSubTab('files')}
            >
              My Uploaded Files
            </button>
            <button
              className={`tab-item${recordsSubTab === 'dop' ? ' tab-item--active' : ''}`}
              onClick={() => setRecordsSubTab('dop')}
            >
              D.O.P.
            </button>
            <button
              className={`tab-item${recordsSubTab === 'ai-data' ? ' tab-item--active' : ''}`}
              onClick={() => setRecordsSubTab('ai-data')}
            >
              My UHC Data Set
            </button>
            <button
              className={`tab-item${recordsSubTab === 'exclusions' ? ' tab-item--active' : ''}`}
              onClick={() => setRecordsSubTab('exclusions')}
            >
              Auto-Excluded
            </button>
            <button
              className={`tab-item${recordsSubTab === 'audit' ? ' tab-item--active' : ''}`}
              onClick={() => setRecordsSubTab('audit')}
            >
              UHC Audit
            </button>
          </div>
        </div>
      </div>

      <div className="page-scroll-content">
      {/* Tab 1: Health Records Hub */}
      {recordsSubTab === 'health-records' && (
        <HealthRecordsHub fetchWithAuth={fetchWithAuth} />
      )}

      {/* Tab 2: Medical Files */}
      {recordsSubTab === 'files' && (
        <>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Medical Records / Files</h3>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowMedicalFileForm(true)}
              >
                Add Record
              </button>
            </div>

            {medicalFiles.length === 0 ? (
              <p>No medical records uploaded. Add your first file!</p>
            ) : (
              <div>
                {medicalFiles.map(file => (
                  <div key={file.id} className="objective-item">
                    <div className="objective-content">
                      <h4>{file.record_name || file.title}</h4>
                      <p>{file.description}</p>
                      {file.record_date && (
                        <p>Date: {formatDateOnly(file.record_date)}</p>
                      )}
                    </div>
                    <div className="objective-actions">
                      {file.has_file && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleViewMedicalFile(file)}
                        >
                          View
                        </button>
                      )}
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEditMedicalFile(file)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteMedicalFile(file.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {showMedicalFileForm && (
            <MedicalFileForm
              onClose={closeMedicalFileForm}
              onSave={loadMedicalFiles}
              fetchWithAuth={fetchWithAuth}
              editingMedicalFile={editingMedicalFile}
            />
          )}

          {/* Medical File Viewer Modal */}
          {viewingMedicalFile && (
            <div className="modal-overlay" onClick={closeMedicalFileViewer}>
              <div className="modal-content modal-content-lg" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{viewingMedicalFile.record_name || 'Medical File'}</h2>
                  <button className="modal-close" onClick={closeMedicalFileViewer}>&times;</button>
                </div>
                <div className="modal-body" style={{ padding: 0, position: 'relative', minHeight: '70vh' }}>
                  {medicalFileLoading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '16px', background: 'white', zIndex: 2 }}>
                      <div className="spinner" style={{ width: '36px', height: '36px', borderWidth: '3px', borderColor: 'rgba(30,58,95,0.2)', borderTopColor: '#1E3A5F' }}></div>
                      <p style={{ color: '#1E3A5F', fontWeight: 500, margin: 0 }}>Decrypting File for Viewing...</p>
                    </div>
                  )}
                  {medicalFileBlobUrl ? (
                    <iframe
                      src={medicalFileBlobUrl}
                      title={viewingMedicalFile.record_name || 'Medical File'}
                      style={{ width: '100%', minHeight: '70vh', border: 'none' }}
                      onLoad={() => setMedicalFileLoading(false)}
                    />
                  ) : !medicalFileLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', color: '#6b7280' }}>
                      Failed to load file
                    </div>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <p style={{ fontStyle: 'italic', color: '#6b7280', margin: 0, fontSize: '0.85rem' }}>
                    Use the PDF toolbar to Print
                  </p>
                  <button className="btn btn-outline" onClick={closeMedicalFileViewer}>Dismiss</button>
                  <button className="btn btn-primary"
                    onClick={() => handleDownloadFile(viewingMedicalFile.id, viewingMedicalFile.record_name || 'medical-file')}>
                    Download
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Tab 3: D.O.P. — Data Order of Precedence */}
      {recordsSubTab === 'dop' && (
        <div className="card">
          <div className="card-header">
            <h3>Data Order of Precedence (D.O.P.)</h3>
          </div>
          <div className="card-body">
            <p className="fhc-description" style={{ textAlign: 'left', maxWidth: '100%', margin: '0 0 1.25rem' }}>
              When Healist receives your health data from multiple sources, the D.O.P. engine determines
              which value takes priority. Higher-tier sources are more trusted and will override lower-tier
              data when conflicts are detected. All conflicts are logged and auditable.
            </p>

            <div className="fhc-dopTable">
              <div className="fhc-dopRow fhc-dopRow--header">
                <span className="fhc-dopTier">Tier</span>
                <span className="fhc-dopSource">Source</span>
                <span className="fhc-dopTrust">Trust</span>
                <span className="fhc-dopExamples">Examples</span>
              </div>
              <div className="fhc-dopRow">
                <span className="fhc-dopTier">1</span>
                <span className="fhc-dopSource">EHR / FHIR Records</span>
                <span className="fhc-dopTrust">100%</span>
                <span className="fhc-dopExamples">Hospital systems via b.well, Apple Health Records, Medicare Blue Button</span>
              </div>
              <div className="fhc-dopRow">
                <span className="fhc-dopTier">2</span>
                <span className="fhc-dopSource">Uploaded Clinical Documents</span>
                <span className="fhc-dopTrust">95%</span>
                <span className="fhc-dopExamples">Lab PDFs, imaging reports, discharge summaries</span>
              </div>
              <div className="fhc-dopRow">
                <span className="fhc-dopTier">3</span>
                <span className="fhc-dopSource">Clinical-Grade Devices</span>
                <span className="fhc-dopTrust">90%</span>
                <span className="fhc-dopExamples">Blood pressure monitors, CGMs, clinical scales</span>
              </div>
              <div className="fhc-dopRow">
                <span className="fhc-dopTier">4</span>
                <span className="fhc-dopSource">Provider / Trainer Responses</span>
                <span className="fhc-dopTrust">85%</span>
                <span className="fhc-dopExamples">Healthcare provider portal reports</span>
              </div>
              <div className="fhc-dopRow">
                <span className="fhc-dopTier">5</span>
                <span className="fhc-dopSource">Consumer Wearables</span>
                <span className="fhc-dopTrust">75%</span>
                <span className="fhc-dopExamples">Apple Health, Fitbit, Garmin, Oura, WHOOP, Withings</span>
              </div>
              <div className="fhc-dopRow">
                <span className="fhc-dopTier">6</span>
                <span className="fhc-dopSource">Manual User Entry</span>
                <span className="fhc-dopTrust">60%</span>
                <span className="fhc-dopExamples">Body characteristics, self-reported data</span>
              </div>
            </div>

            <div className="fhc-dopInfo">
              <h4>How It Works</h4>
              <ul>
                <li><strong>Confidence Score</strong> = Trust Weight x Recency Factor x Completeness</li>
                <li><strong>Conflict Resolution</strong>: When two sources report different values for the same metric, the higher-confidence record wins as the primary value</li>
                <li><strong>Audit Trail</strong>: All conflicts are logged with both values, their sources, and which was selected</li>
                <li><strong>Recency Decay</strong>: Older data gradually loses confidence over time (half-life ~1 year)</li>
                <li><strong>No Data Lost</strong>: Lower-priority values are retained with provenance metadata for reference</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: My AI Data Set */}
      {recordsSubTab === 'ai-data' && renderAiDataTab()}

      {/* Tab 5: UHC Exclusions */}
      {recordsSubTab === 'exclusions' && (
        <UhcExclusions fetchWithAuth={fetchWithAuth} />
      )}

      {/* Tab 6: UHC Audit Reports */}
      {recordsSubTab === 'audit' && (
        <UhcAuditReports fetchWithAuth={fetchWithAuth} />
      )}

      </div>
    </div>
  );
}

export default RecordsPage;
